// Shared client/server contract for the Workout Plan Scanner (#3654).
// The user photographs or screenshots a workout; the server sends the
// image(s) to a vision LLM together with a numbered exercise catalog
// (built-in exercises + the user's custom exercises); the model returns
// the scanned program with each exercise matched to a catalog index; the
// server maps indices back to exercise ids and returns `matchedExerciseId`
// per exercise. Mirrors `scan-equipment-interface.ts`.

// The model registry now lives in the shared scan-models.ts (one registry
// for all scanners). See design-docs/scanner-model-config.md.

// ── Tunable constants ───────────────────────────────────────────────

/** Maximum images per scan request (a long program may span several photos / screenshots). */
export const MAX_WORKOUT_PLAN_SCAN_IMAGES = 5;

// ── The scanned program (the model's JSON contract) ─────────────────
//
// `ScannedWorkout` models the WHOLE scanned program. Each entry in its
// `workouts[]` array is a single day (its `name` is a day label like
// "A" / "Push" / "Day 1"). The model returns this exact JSON shape,
// except that each exercise carries `matchedIndex` (a number into the
// numbered candidate catalog embedded in the prompt, or null); the
// server maps that index to a real exercise id and replaces it with
// `matchedExerciseId` before returning results to the client.

export interface ScannedExercise {
  /** exercise name verbatim, as read off the image */
  name: string;
  /** server-mapped catalog exercise id (built-in `exercise/$…` or custom), or null when the model found no fit */
  matchedExerciseId: string | null;
  /** for UNMATCHED exercises only: the server-mapped id of the closest catalog
   *  substitute (equipment may differ) — shown to users who would rather use an
   *  existing exercise than create a custom one. Omitted when the model found a
   *  real match or no substitute is even close. */
  suggestedReplacementId?: string;
  /** number of working sets */
  sets: number;
  /** target reps — only present when explicitly written on the scanned workout */
  targetReps?: number;
  /** freeform notes read off the image (e.g. "RIR 2") */
  notes?: string;
}

export interface ScannedWorkoutDay {
  /** day label as read (A/B/C, "Push", "Day 1", …) */
  name: string;
  exercises: ScannedExercise[];
}

export interface ScannedWorkout {
  workouts: ScannedWorkoutDay[];
  /** 0..1 overall confidence in the extraction */
  confidence: number;
  /** set when there is no readable workout in the images */
  noWorkoutReason?: NoWorkoutReason;
}

// ── Status / errors ─────────────────────────────────────────────────

export enum ScanWorkoutPlanStatus {
  Success = 'success',
  Blurry = 'blurry',
  NoWorkout = 'no_workout',
}

/** Reasons the AI returned no usable workout (subset of ScanWorkoutPlanStatus that excludes Success). */
export type NoWorkoutReason = ScanWorkoutPlanStatus.Blurry | ScanWorkoutPlanStatus.NoWorkout;

export enum ScanWorkoutPlanError {
  AuthRequired = 'auth_required',
  NoImage = 'no_image',
  TooManyImages = 'too_many_images',
  ImageTooLarge = 'image_too_large',
  AllModelsRestricted = 'all_models_restricted',
  UnknownModel = 'unknown_model',
  ProviderError = 'provider_error',
  ProviderTimeout = 'provider_timeout',
  // the model stopped before returning a complete response — typically the output-token
  // budget was exhausted (a thinking model spent it reasoning). Transient: retrying the
  // same images usually succeeds, so the client keeps the captured photos.
  IncompleteResponse = 'incomplete_response',
}

// ── Quota ───────────────────────────────────────────────────────────

export enum ScanWorkoutPlanQuotaRejection {
  AccountLocked = 'account_locked',
  RateLimited = 'rate_limited',
  DailyCooldown = 'daily_cooldown',
  QualityWarning = 'quality_warning',
  // the whole app's daily AI-scan ceiling was reached, not this user's own quota (#4032)
  GlobalCap = 'global_cap',
}

export interface ScanWorkoutPlanQuota {
  scansToday: number;
  rejection?: ScanWorkoutPlanQuotaRejection;
  cooldownUntil_utc?: string;
  lockedUntil_utc?: string;
}

// ── Client → Server params ──────────────────────────────────────────

/** A custom exercise of the requesting user, merged into the matching catalog the prompt embeds. */
export interface WorkoutPlanScanCustomExercise {
  /** the custom exercise's doc id (e.g. `exercise/c…`) */
  id: string;
  name: string;
  shortname?: string;
}

export interface ScanWorkoutPlanParams {
  imagesBase64: string[];
  /** a model key from SCAN_MODELS, or 'all' (moderator-only multi-model dispatch) */
  model?: string;
  /** the user's custom exercises, merged into the matching catalog */
  customExercises?: WorkoutPlanScanCustomExercise[];
  // The user's active UI language (#4206). This scanner's AI output is not
  // localized today: day labels, exercise names and notes are copied word-for-word
  // off the photographed program, and the matched exercise's own name comes from
  // our catalog (already localized client-side). The field is carried so a future
  // framing hook can read `params.locale` without another client release.
  locale?: string;
}

// ── Server → Client results ─────────────────────────────────────────

/** Per-model payload returned when `params.model === 'all'` (multi-model dispatch). */
export interface ScanWorkoutPlanModelResult {
  workout?: ScannedWorkout;
  durationMs?: number;
  inputTokens?: number;
  outputTokens?: number;
  error?: string;
}

export interface ScanWorkoutPlanResults {
  // single-model response
  workout?: ScannedWorkout;
  status?: ScanWorkoutPlanStatus;
  modelKey?: string;

  // multi-model response (model='all')
  modelResults?: { [modelKey: string]: ScanWorkoutPlanModelResult };
  scanId?: string;

  // quota piggybacks on every scan response
  quota?: ScanWorkoutPlanQuota;

  error?: ScanWorkoutPlanError | string;
}
